import styled from "styled-components"
import { FlexBox } from "../generic/Layout"
import DisplayElementType from "./DisplayElementType"
import { resolveVisibility } from "../../../../common/model/condition/evaluate"

// `weight` and `basis` are two independent inputs, not two spellings of one.
// `flex: <weight>` is shorthand for `flex-grow: <weight>; flex-shrink: 1;
// flex-basis: 0%`, and the `flex-basis` line then overrides that 0%. So `basis`
// is the column's starting width and `weight` only shares out whatever space is
// left over — which is none at all when a row's bases already sum to 100%.
// They were called `flex` and `grow`, where `grow` was the one that meant basis.
const DisplayCol = styled.div`
    flex: ${(props) => props.$weight};
    flex-basis: ${(props) => props.$basis};
    text-align: ${(props) => props.$align ? props.$align : 'start'};
    margin: ${(props) => props.$margin ?? '0'};

    /*
     * A little air between the rows of a column.
     *
     * Each element renders a <div> and <Text>'s own 0.25rem block margins
     * collapse against its neighbours, so a run of six fields arrived as one
     * dense block with nothing to separate a date from a state from a book. The
     * gap is small on purpose — this is a dense record view, not a form.
     */
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
`

/*
 * `stackOnMobile` on the display section: on a phone the columns go one under
 * the other, each the full width and read from the left, instead of three
 * narrow columns side by side. A desktop is unchanged.
 */
const Columns = styled(FlexBox)`
    @media (max-width: ${({ theme }) => theme.breakpoint.xs}) {
        ${({ $stack }) => $stack && `
            flex-direction: column;
            gap: 0.35rem;

            > div {
                flex: 0 0 auto;
                text-align: start;
            }
        `}
    }
`

const DisplayColumns = ({ config, displayData, currency }) => (
    <Columns $stack={!!config.stackOnMobile}>
        {config.columns.map((column, i) => (
            <DisplayCol key={i} $weight={column.weight} $basis={column.basis} $align={column.align} $margin={column.margin}>
                {/*
                  * A display element can be conditional, and the predicate is
                  * answered by the row being drawn — on a line that is the line,
                  * not the document. A discount has nothing to say on a line
                  * that has none, and an unconditioned element would print an
                  * empty value on every other row.
                  */}
                {column.elements
                    ?.filter(field => resolveVisibility(field, { values: displayData || {}, context: {} }) === 'show')
                    .map((field, j) => (
                    <DisplayElementType
                        key={field.value ?? field.debitField ?? j}
                        displayData={displayData}
                        element={field}
                        $variant={field.variant}
                        currency={currency}
                    />
                ))}
            </DisplayCol>
        ))}
    </Columns>
)

export default DisplayColumns
